<template>
  <Transition name="slide-fadeUp" appear>
    <div v-if="visible" class="modal-center">
      <div class="modal-content">
        <div class="left-content">
          <div class="modal-right">
            <div class="modal-right-header">
              <span class="step-text">{{$t('step')}}: {{ currentStep }}</span>
              <span class="step-title">{{currentStepDescription}}</span>
            </div>

            <div class="modal-right-body-container" ref="scrollContainer">
              <div class="modal-right-body" @scroll="handleScroll">
              <div class="step-description">
                <img class="hint-image" src="@/assets/images/lightbulbBlue.svg">
                <span class="hint-text"><span style="font-weight: bold">{{$t('hint')}}: </span>{{currentStepLongDescription}}</span>
              </div>

              <!-- Step 1: Avatar, Name, Description, About -->
              <div class="step-inputs" v-if="currentStep === 1">
                <div class="form-row">
                  <div class="avatar-section">
                    <div class="avatar-holder">
                      <img :src="agentData.avatarUrl || '/images/profile.svg'" class="avatar-preview">
                      <div class="avatar-upload" v-if="!isUploadingAvatar">
                        <input
                            type="file"
                            ref="avatarInput"
                            @change="handleAvatarUpload"
                            accept="image/*"
                            style="display: none"
                        />
                        <button @click="$refs.avatarInput.click()" class="upload-avatar-btn">
                          <img src="@/assets/images/camera.svg" v-if="!agentData.avatarUrl">
                          {{ agentData.avatarUrl ? $t('change_avatar') : $t('upload_avatar') }}
                        </button>
                      </div>
                      <div v-else class="uploading-avatar">
                        <span>{{$t('uploading')}}</span>
                      </div>
                    </div>
                  </div>

                  <div class="form-column">
                    <label class="form-label">{{$t('agent_name')}}</label>
                    <input
                        type="text"
                        class="form-input"
                        :placeholder="$t('agent_name_placeholder')"
                        v-model="agentData.name"
                        maxlength="50"
                    />

                    <label class="form-label mt-3">{{$t('description')}}</label>
                    <textarea
                        v-textarea-autoresize
                        class="form-input"
                        :placeholder="$t('description_placeholder')"
                        v-model="agentData.description"
                        maxlength="150"
                        rows="3"
                    />

                    <label class="form-label mt-3">{{$t('about')}} ({{ agentData.about ? agentData.about.length : 0 }}/500)</label>
                    <textarea
                        v-textarea-autoresize
                        class="form-input"
                        :placeholder="$t('about_placeholder')"
                        v-model="agentData.about"
                        maxlength="500"
                        rows="4"
                    />
                  </div>
                </div>
              </div>

              <!-- Step 2: File Upload -->
              <div class="step-inputs" v-if="currentStep === 2">
                <div class="upload-section">
                  <div class="single-filedrop">
                    <FileDrop
                      type="allFiles"
                      size="normal"
                      :on-file-selected="handleFileSelected"
                      :image-editor-enabled="false"
                      :multiple="true"
                    />
                  </div>

                  <div class="file-info-section">
                    <div class="file-counter">
                      {{ uploadedFiles.length }} / 5 {{$t('files_uploaded')}}
                    </div>

                    <div v-if="uploadedFiles.length > 0" class="file-list">
                      <div
                        v-for="(file, index) in uploadedFiles"
                        :key="index"
                        class="file-list-item"
                      >
                        <div class="file-list-info">
                          <div class="file-list-name">{{ file.name }}</div>
                          <div class="file-list-size">{{ formatFileSize(file.size) }}</div>
                        </div>
                        <button @click="removeFileFromList(index)" class="remove-file-list-btn">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" stroke-width="2"/>
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div v-if="uploadedFiles.length >= 5" class="file-limit-warning">
                      ⚠️ {{$t('maximum_5_files_reached')}}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Step 3: Questions and Answers -->
              <div class="step-inputs" v-if="currentStep === 3">
                <div class="qa-section">
                  <div class="qa-form-container">
                    <div class="qa-input-group">
                      <div class="input-wrapper">
                        <label class="form-label">{{$t('question')}}</label>
                        <input
                          type="text"
                          class="form-input"
                          :placeholder="$t('enter_question_placeholder')"
                          v-model="newQuestion"
                        />
                      </div>

                      <div class="input-wrapper">
                        <label class="form-label">{{$t('answer')}}</label>
                        <textarea
                          v-textarea-autoresize
                          class="form-input"
                          :placeholder="$t('enter_answer_placeholder')"
                          v-model="newAnswer"
                          rows="3"
                        />
                      </div>

                      <button
                        @click="addQAPair"
                        class="primary-button"
                        :disabled="!newQuestion.trim() || !newAnswer.trim()"
                      >
                        {{$t('add_qa')}}
                      </button>
                    </div>
                  </div>

                  <div v-if="agentData.qaPairs.length > 0" class="qa-list-container">
                    <div class="qa-list-header">
                      <span>{{$t('added_questions')}} ({{ agentData.qaPairs.length }})</span>
                    </div>

                    <div class="qa-list">
                      <div
                        v-for="(qa, index) in agentData.qaPairs"
                        :key="index"
                        class="qa-item"
                      >
                        <div class="qa-header" @click="toggleQA(index)">
                          <div class="qa-header-content">
                            <img
                              src="@/assets/images/arrowdown.svg"
                              class="qa-arrow"
                              :class="expandedQA === index ? 'rotated' : ''"
                            />
                            <span class="qa-question">{{ qa.question }}</span>
                          </div>
                          <button @click.stop="removeQA(index)" class="remove-qa-btn">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" stroke-width="2"/>
                            </svg>
                          </button>
                        </div>
                        <div v-if="expandedQA === index" class="qa-answer">
                          {{ qa.answer }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Step 4: Context -->
              <div class="step-inputs" v-if="currentStep === 4">
                <label class="form-label">{{$t('agent_context')}}</label>
                <textarea
                    v-textarea-autoresize
                  class="form-input"
                  :placeholder="$t('context_placeholder')"
                  v-model="agentData.context"
                  rows="8"
                />

                <div class="context-suggestions">
                  <span class="suggestions-label">{{$t('suggestions')}}:</span>
                  <div class="suggestion-tags">
                    <span
                      v-for="suggestion in contextSuggestions"
                      :key="suggestion"
                      class="suggestion-tag"
                      @click="addContextSuggestion(suggestion)"
                    >
                      {{ $t(suggestion) }}
                      <img src="@/assets/images/plusBlack.svg"/>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>

          <div class="separator" />

          <div class="modal-left">
            <button class="dark-button-outline" @click="handlePrev">
              <span v-if="currentStep === 1">{{$t('cancel_button')}}</span>
              <span v-else>{{$t('go_back_button')}}</span>
            </button>

            <div class="stepper-wrapper">
              <div
                v-for="step in 4"
                :key="step"
                class="step-wrapper"
                :class="getStepClass(step)"
              >
                <div class="step-circle">
                  <span v-if="currentStep > step">✓</span>
                  <span v-else>{{ step }}</span>
                </div>
              </div>
            </div>

            <button
              class="primary-button"
              :class="!nextEnabled && 'disabled'"
              @click="handleNext"
              :disabled="!nextEnabled"
            >
              <span v-if="currentStep < 4">{{$t('next')}}</span>
              <span v-else>{{$t('create_agent')}}</span>
            </button>
          </div>
        </div>

        <div class="right-banner" :class="`banner-step-${currentStep}`">
          <div class="cross-holder">
            <div @click="handleClose" class="close-btn">
              <img class="cross" src="../../assets/images/cross.svg" />
            </div>
          </div>
          <img class="nano-image" :src="currentStepImage">
          <div class="right-text-holder">
            <h3>{{currentStepData.title}}</h3>
            <span>{{currentStepFact}}</span>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
import agentService from "../../service/agentService";
import {handleErrorResponse} from "@/utils/utils";
import FileDrop from "@/components/FileDrop.vue";
import AgentCreateImage1 from '@/assets/images/agentCreate1.svg';
import AgentCreateImage2 from '@/assets/images/agentCreate2.svg';
import AgentCreateImage3 from '@/assets/images/agentCreate3.svg';
import AgentCreateImage4 from '@/assets/images/agentCreate4.svg';

export default {
  components: {
    FileDrop
  },

  props: {
    visible: {
      type: Boolean,
      default: false
    },
    onHide: {
      type: Function,
      default: () => {}
    },
    onAction: {
      type: Function,
      default: () => {}
    }
  },

  data() {
    return {
      currentStep: 1,
      isUploadingAvatar: false,
      expandedQA: null,
      newQuestion: '',
      newAnswer: '',

      agentData: {
        name: '',
        description: '',
        about: '',
        avatarUrl: '',
        context: '',
        qaPairs: []
      },

      uploadedFiles: [],

      contextSuggestions: [
        'helpful_assistant',
        'professional_tone',
        'friendly_approach',
        'technical_expert',
        'customer_service',
        'educational_tutor'
      ],

      steps: {
        1: {
          title: this.$t('basic_info_step'),
          description: this.$t('basic_info_step'),
          longDescription: this.$t('basic_info_hint'),
          fact: this.$t('agent_fact_1'),
          image: AgentCreateImage1
        },
        2: {
          title: this.$t('knowledge_step'),
          description: this.$t('knowledge_step'),
          longDescription: this.$t('knowledge_hint'),
          fact: this.$t('agent_fact_2'),
          image: AgentCreateImage2
        },
        3: {
          title: this.$t('qa_step'),
          description: this.$t('qa_step'),
          longDescription: this.$t('qa_hint'),
          fact: this.$t('agent_fact_3'),
          image: AgentCreateImage3
        },
        4: {
          title: this.$t('context_step'),
          description: this.$t('context_step'),
          longDescription: this.$t('context_hint'),
          fact: this.$t('agent_fact_4'),
          image: AgentCreateImage4
        }
      }
    }
  },

  computed: {
    currentStepData() {
      return this.steps[this.currentStep];
    },
    currentStepDescription() {
      return this.steps[this.currentStep]?.description;
    },
    currentStepLongDescription() {
      return this.steps[this.currentStep]?.longDescription;
    },
    currentStepFact() {
      return this.steps[this.currentStep]?.fact;
    },
    currentStepImage() {
      return this.steps[this.currentStep]?.image;
    },

    nextEnabled() {
      switch (this.currentStep) {
        case 1:
          return this.agentData.name.trim() && this.agentData.description.trim();
        case 2:
          return true; // File upload is optional
        case 3:
          return true; // Q&A is optional
        case 4:
          return this.agentData.context.trim();
        default:
          return false;
      }
    }
  },

  methods: {
    handleScroll(event) {
      const element = event.target;
      const container = this.$refs.scrollContainer;

      if (!container) return;

      // Check if scrolled from top
      const scrolledFromTop = element.scrollTop > 10;

      // Check if scrolled to bottom (with 10px threshold)
      const scrolledToBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 10;

      // Add/remove CSS classes based on scroll position
      if (scrolledFromTop) {
        container.classList.add('scrolled-top');
      } else {
        container.classList.remove('scrolled-top');
      }

      if (!scrolledToBottom && element.scrollHeight > element.clientHeight) {
        container.classList.add('scrolled-bottom');
      } else {
        container.classList.remove('scrolled-bottom');
      }
    },

    handlePrev() {
      if (this.currentStep === 1) {
        this.resetForm();
        this.onHide();
      } else {
        this.currentStep--;
      }
    },

    handleNext() {
      if (this.currentStep === 4) {
        this.createAgent();
      } else {
        this.currentStep++;
      }
    },


    handleClose() {
      this.resetForm();
      this.onHide();
    },

    resetForm() {
      this.currentStep = 1;
      this.agentData = {
        name: '',
        description: '',
        about: '',
        avatarUrl: '',
        context: '',
        qaPairs: []
      };
      this.uploadedFiles = [];
      this.newQuestion = '';
      this.newAnswer = '';
      this.expandedQA = null;
    },

    getStepClass(step) {
      if (step < this.currentStep) return 'completed';
      if (step === this.currentStep) return 'current';
      return '';
    },

    async handleAvatarUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        this.$snackbar?.add({
          type: 'error',
          text: this.$t('please_select_image')
        });
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        this.$snackbar?.add({
          type: 'error',
          text: this.$t('image_too_large')
        });
        return;
      }

      try {
        this.isUploadingAvatar = true;
        const formData = new FormData();
        formData.append('avatar', file);

        const response = await agentService.uploadAgentAvatar(formData);

        if (response.data.success) {
          this.agentData.avatarUrl = response.data.avatarUrl;
          this.$snackbar?.add({
            type: 'success',
            text: this.$t('avatar_uploaded')
          });
        } else {
          throw new Error(response.data.error || 'Upload failed');
        }
      } catch (error) {
        console.error('Avatar upload error:', error);
        this.$snackbar?.add({
          type: 'error',
          text: this.$t('avatar_upload_failed')
        });
      } finally {
        this.isUploadingAvatar = false;
        event.target.value = '';
      }
    },

    handleFileSelected(file) {
      // Check if we've reached the file limit
      if (this.uploadedFiles.length >= 5) {
        this.$snackbar?.add({
          type: 'error',
          text: this.$t('maximum_5_files_reached')
        });
        return;
      }

      // Add file to uploaded files list
      if (file) {
        this.uploadedFiles.push(file);
        this.$snackbar?.add({
          type: 'success',
          text: `${file.name} ${this.$t('file_added')}`
        });
      }
    },

    removeFileFromList(index) {
      const removedFile = this.uploadedFiles[index];
      this.uploadedFiles.splice(index, 1);

      if (removedFile) {
        this.$snackbar?.add({
          type: 'info',
          text: `${removedFile.name} ${this.$t('file_removed')}`
        });
      }
    },


    formatFileSize(bytes) {
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    },

    addQAPair() {
      if (this.newQuestion.trim() && this.newAnswer.trim()) {
        this.agentData.qaPairs.push({
          question: this.newQuestion.trim(),
          answer: this.newAnswer.trim()
        });
        this.newQuestion = '';
        this.newAnswer = '';
      }
    },

    removeQA(index) {
      this.agentData.qaPairs.splice(index, 1);
      if (this.expandedQA === index) {
        this.expandedQA = null;
      }
    },

    toggleQA(index) {
      this.expandedQA = this.expandedQA === index ? null : index;
    },

    addContextSuggestion(suggestion) {
      const text = this.$t(suggestion);
      if (!this.agentData.context.includes(text)) {
        this.agentData.context = this.agentData.context
          ? `${this.agentData.context}\n${text}`
          : text;
      }
    },

    async createAgent() {
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        const formData = new FormData();

        // Add agent data
        formData.append('name', this.agentData.name);
        formData.append('description', this.agentData.description);
        formData.append('about', this.agentData.about || '');
        formData.append('avatarUrl', this.agentData.avatarUrl || '');
        formData.append('context', this.agentData.context);
        formData.append('userId', user.userId);
        formData.append('companyId', user.companyId);
        formData.append('qaPairs', JSON.stringify(this.agentData.qaPairs));

        // Add files
        this.uploadedFiles.forEach((file) => {
          if (file) {
            formData.append('documents', file);
          }
        });

        await this.onAction(formData);
        this.resetForm();
        this.onHide();

      } catch (error) {
        handleErrorResponse(error, this.$snackbar);
      }
    }
  },

};
</script>

<style scoped>
.modal-content {
  position: relative;
  background-color: white;
  width: 70%;
  height: 100%;
  border-radius: 12px;
  padding: 0px !important;
  overflow: hidden;
  max-height: calc(100vh - 48px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: row;
}

.left-content {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}

.modal-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.modal-right-header {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  border: none;
  gap: 2px;
  padding: 24px 24px 12px 24px;
  flex-shrink: 0;
}

.step-text {
  color: #667085;
  font-size: 16px;
  font-family: euclid_medium, sans-serif;
}

.step-title {
  font-family: euclid_semi_bold, sans-serif;
  color: #040C2D;
  font-size: 24px;
  font-style: normal;
  line-height: 36px;
}

.modal-right-body-container {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
}

.modal-right-body-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 24px;
  right: 24px;
  height: 24px;
  background: linear-gradient(to bottom,
    rgba(255, 255, 255, 1) 0%,
    rgba(255, 255, 255, 0.9) 30%,
    rgba(255, 255, 255, 0) 100%);
  pointer-events: none;
  z-index: 10;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.modal-right-body-container::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 24px;
  right: 24px;
  height: 24px;
  background: linear-gradient(to top,
    rgba(255, 255, 255, 1) 0%,
    rgba(255, 255, 255, 0.9) 30%,
    rgba(255, 255, 255, 0) 100%);
  pointer-events: none;
  z-index: 10;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.modal-right-body-container.scrolled-top::before {
  opacity: 1;
}

.modal-right-body-container.scrolled-bottom::after {
  opacity: 1;
}

.modal-right-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px 24px 24px 24px;
  overflow-y: auto;
  flex: 1;
  width: 100%;
}

.step-description {
  font-family: euclid_medium, sans-serif;
  color: #667085;
  font-size: 16px;
  font-style: normal;
  line-height: 22px;
  background-color: #EBEDFB;
  border-radius: 8px;
  padding: 12px;
  margin-right: 4px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.hint-image {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  margin-top: 2px;
}

.hint-text {
  color: #101828;
  font-size: 12px;
}

.step-inputs {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-top: 20px;
}

/* Avatar Section */
.avatar-section {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.avatar-holder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.avatar-preview {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #EAECF0;
}

.upload-avatar-btn {
  background-color: #F2F4F7;
  border: 1px solid #D0D5DD;
  color: #667085;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 12px;
  cursor: pointer;
  font-family: euclid_medium, sans-serif;
  display: flex;
  align-items: center;
  gap: 6px;
}

.upload-avatar-btn:hover {
  background-color: #E4E7EC;
}

.uploading-avatar {
  font-size: 12px;
  color: #667085;
  font-family: euclid_medium, sans-serif;
}

/* Single FileDrop */
.single-filedrop {
  margin-top: 10px;
  margin-bottom: 20px;
}

.file-info-section {
  margin-top: 20px;
}

.file-counter {
  font-size: 14px;
  font-family: euclid_medium, sans-serif;
  color: #374151;
  margin-bottom: 16px;
}

.file-list {
  margin-top: 12px;
}

.file-list-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background-color: white;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  margin-bottom: 6px;
}

.file-list-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.file-list-name {
  font-size: 12px;
  font-family: euclid_medium, sans-serif;
  color: #374151;
  margin-bottom: 2px;
}

.file-list-size {
  font-size: 10px;
  font-family: euclid_regular, sans-serif;
  color: #9CA3AF;
}

.remove-file-list-btn {
  background: none;
  border: none;
  color: #EF4444;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-file-list-btn:hover {
  background-color: #FEF2F2;
}

.file-limit-warning {
  color: #D97706;
  font-size: 12px;
  text-align: center;
  margin-top: 8px;
  font-family: euclid_medium, sans-serif;
}

/* QA Section */
.qa-form-container {
  margin-bottom: 24px;
}

.qa-input-group {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.qa-list-container {
  margin-top: 24px;
}

.qa-list-header {
  margin-bottom: 12px;
}

.qa-list-header span {
  font-size: 14px;
  color: #374151;
  font-family: euclid_medium, sans-serif;
}

.qa-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.qa-item {
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
}

.qa-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  background-color: #F9FAFB;
  transition: background-color 0.2s ease;
}

.qa-header:hover {
  background-color: #F3F4F6;
}

.qa-header-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.qa-arrow {
  width: 12px;
  height: 12px;
  transform: rotate(-90deg);
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.qa-arrow.rotated {
  transform: rotate(0deg);
}

.qa-question {
  font-size: 14px;
  color: #374151;
  font-family: euclid_medium, sans-serif;
  line-height: 1.4;
}

.remove-qa-btn {
  background: none;
  border: none;
  color: #9CA3AF;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-qa-btn:hover {
  color: #EF4444;
  background-color: #FEF2F2;
}

.qa-answer {
  padding: 16px;
  font-size: 14px;
  color: #6B7280;
  font-family: euclid_regular, sans-serif;
  border-top: 1px solid #F3F4F6;
  background-color: white;
  line-height: 1.5;
}

/* Context Suggestions */
.context-suggestions {
  margin-top: 16px;
}

.suggestions-label {
  font-size: 12px;
  color: #667085;
  font-family: euclid_medium, sans-serif;
  margin-bottom: 8px;
  display: block;
}

.suggestion-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.suggestion-tag {
  padding: 6px 12px;
  border: 1px solid #EAECF0;
  border-radius: 3000px;
  font-size: 12px;
  color: #667085;
  font-family: euclid_medium, sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
}

.suggestion-tag:hover {
  background-color: #EAECF0;
  border-color: #040C2D;
  color: #040C2D;
}

.suggestion-tag img {
  width: 12px;
  height: 12px;
}

/* Navigation */
.separator {
  width: 100%;
  background-color: #EAECF0;
  height: 1px;
  flex-shrink: 0;
}

.modal-left {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 28px;
  width: 100%;
  gap: 50px;
  flex-shrink: 0;
  background-color: white;
}

.stepper-wrapper {
  display: flex;
  flex-direction: row;
  gap: 16px;
}

.step-wrapper {
  display: flex;
  align-items: center;
}

.step-circle {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #F2F4F7;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.3s ease;
  font-size: 12px;
  font-family: euclid_medium, sans-serif;
  color: #667085;
}

.step-wrapper.current .step-circle {
  background-color: #040C2D;
  color: white;
}

.step-wrapper.completed .step-circle {
  background-color: #10B981;
  color: white;
}

/* Right Banner */
.right-banner {
  display: flex;
  flex-direction: column;
  width: 40%;
  background-color: #F9F6F2;
}

.banner-step-1 {
  background-color: #F9F6F2;
}

.banner-step-2 {
  background-color: #EBEDFB;
}

.banner-step-3 {
  background-color: #EFF8EF;
}

.banner-step-4 {
  background-color: #FFF4EE;
}

.cross-holder {
  display: flex;
  width: 100%;
  justify-content: flex-end;
  padding: 24px 24px 0 24px;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  background-color: #F2F4F7;
  border-radius: 3000px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cross {
  padding: 8px;
}

.nano-image {
  margin-top: 20%;
  padding: 15px 0 0 50px;
  max-width: 300px;
}

.right-text-holder {
  display: flex;
  flex-direction: column;
  align-self: center;
  margin-top: 5%;
  text-align: center;
  gap: 1rem;
  padding: 0 20px;
}

.right-text-holder h3 {
  font-size: 24px;
  color: #101828;
  font-weight: bold;
  font-family: euclid_semi_bold, sans-serif;
}

.right-text-holder span {
  font-size: 14px;
  color: #475467;
  padding: 5px;
  font-family: euclid_regular, sans-serif;
}


/* Scrollbar */
.modal-right-body::-webkit-scrollbar {
  width: 8px;
}

.modal-right-body::-webkit-scrollbar-track {
  background-color: transparent;
}

.modal-right-body::-webkit-scrollbar-thumb {
  background-color: rgba(4, 12, 45, 0.2);
  border-radius: 10px;
}

.modal-right-body::-webkit-scrollbar-thumb:hover {
  background-color: rgba(4, 12, 45, 0.4);
}

.form-row {
  display: flex;
  gap: 20px;
}

.form-column {
  display: flex;
  flex-direction: column;
  flex: 1;
}

/* Responsive */
@media (max-width: 768px) {
  .modal-content {
    width: 90%;
    flex-direction: column;
  }

  .right-banner {
    width: 100%;
    min-height: 200px;
  }


  .modal-left {
    gap: 20px;
  }
}
</style>
